import { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  Fade,
  Popover,
  IconButton,
} from "@mui/material";

import {
  CalendarToday,
  Add,
  ErrorOutline,
  Event,
  Inbox,
  KeyboardArrowDown,
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";

import {
  colors,
  headerStyles,
  calendarStyles,
  datePickerStyles,
} from "./Followup.styles";

const DAYS_OF_WEEK = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const quickOptions = [
  { label: 'Today', value: 'today' },
  { label: 'Tomorrow', value: 'tomorrow' },
  { label: 'This Week', value: 'this_week' },
  { label: 'Next Week', value: 'next_week' },
];

// Animated count-up component
const AnimatedCounter = ({ value, duration = 600 }) => {
  const [display, setDisplay] = useState(0);
  const prevValue = useRef(0);
  const rafRef = useRef(null);

  useEffect(() => {
    const start = prevValue.current;
    const end = value;
    if (start === end) return;

    const startTime = performance.now();

    const animate = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease-out cubic for smooth deceleration
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(start + (end - start) * eased);
      setDisplay(current);

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        prevValue.current = end;
      }
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [value, duration]);

  return display;
};

const FollowupHeader = ({
  onDateChange,
  onNewTask,
  totalPendingTasks,
  stats,
}) => {
  const [dateAnchorEl, setDateAnchorEl] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [rangeStart, setRangeStart] = useState(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0); return d;
  });
  const [rangeEnd, setRangeEnd] = useState(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0); return d;
  });
  const [hoverDate, setHoverDate] = useState(null);
  const [isSelectingRange, setIsSelectingRange] = useState(false);
  const [activeQuickOption, setActiveQuickOption] = useState('today');

  const today = new Date();

  const handleDateClick = (event) => {
    setDateAnchorEl(event.currentTarget);
  };

  const handleDateClose = () => {
    setDateAnchorEl(null);
  };

  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const getCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const days = [];

    // Determine effective range for highlighting
    const effectiveEnd = rangeEnd || (isSelectingRange ? hoverDate : null);
    let rStart = rangeStart;
    let rEnd = effectiveEnd;
    if (rStart && rEnd && rEnd < rStart) { const tmp = rStart; rStart = rEnd; rEnd = tmp; }

    for (let i = 0; i < firstDay; i++) {
      days.push({ day: null, disabled: true });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      const dateStr = date.toDateString();

      const isRangeStart = !!(rStart && dateStr === rStart.toDateString());
      const isRangeEnd = !!(rEnd && dateStr === rEnd.toDateString());
      const isInRange = !!(rStart && rEnd && date >= rStart && date <= rEnd && !isRangeStart && !isRangeEnd);

      days.push({
        day,
        date,
        isToday: dateStr === today.toDateString(),
        isSelected: isRangeStart || isRangeEnd,
        isInRange,
        isRangeStart,
        isRangeEnd,
        disabled: false,
      });
    }

    return days;
  };

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleDayClick = (dayInfo) => {
    if (dayInfo.disabled || !dayInfo.date) return;

    if (!isSelectingRange) {
      // First click — start the range
      setRangeStart(dayInfo.date);
      setRangeEnd(null);
      setIsSelectingRange(true);
      setHoverDate(null);
      setActiveQuickOption(null);
    } else {
      // Second click — complete the range
      let start = rangeStart;
      let end = dayInfo.date;
      if (end < start) { const tmp = start; start = end; end = tmp; }
      setRangeStart(start);
      setRangeEnd(end);
      setIsSelectingRange(false);
      setHoverDate(null);
      onDateChange(null, null, { start, end });
      handleDateClose();
    }
  };

  const handleDayHover = (dayInfo) => {
    if (!isSelectingRange || dayInfo.disabled || !dayInfo.date) return;
    setHoverDate(dayInfo.date);
  };

  const handleQuickSelect = (option) => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    let start = new Date(now);
    let end = new Date(now);

    switch (option.value) {
      case 'today':
        break;
      case 'tomorrow':
        start.setDate(start.getDate() + 1);
        end = new Date(start);
        break;
      case 'this_week':
        start.setDate(now.getDate() - now.getDay());
        end = new Date(start);
        end.setDate(start.getDate() + 6);
        break;
      case 'next_week':
        start.setDate(now.getDate() + (7 - now.getDay()));
        end = new Date(start);
        end.setDate(start.getDate() + 6);
        break;
      default:
        break;
    }

    setRangeStart(start);
    setRangeEnd(end);
    setIsSelectingRange(false);
    setHoverDate(null);
    setActiveQuickOption(option.value);
    setCurrentMonth(start.getMonth());
    setCurrentYear(start.getFullYear());
    onDateChange(null, option.value, { start, end });
    handleDateClose();
  };

  const formatDateButtonLabel = () => {
    if (!rangeStart) return 'Select Date';
    const opts = { month: 'short', day: 'numeric' };
    const startStr = rangeStart.toLocaleDateString('en-US', opts);

    if (!rangeEnd || rangeStart.toDateString() === rangeEnd.toDateString()) {
      const isToday = rangeStart.toDateString() === today.toDateString();
      return isToday ? `Today, ${startStr}` : startStr;
    }

    const endStr = rangeEnd.toLocaleDateString('en-US', opts);
    return `${startStr} – ${endStr}`;
  };

  const statsData = [
    {
      icon: <ErrorOutline sx={{ color: "#ff6b6b", fontSize: 24 }} />,
      label: "OVERDUE",
      value: stats?.overdue ?? 0,
      iconBg: "#ffe5e5",
    },
    {
      icon: <Event sx={{ color: "#7c3aed", fontSize: 24 }} />,
      label: "DUE TODAY",
      value: stats?.dueToday ?? 0,
      iconBg: "#f3e8ff",
    },
    {
      icon: <Inbox sx={{ color: "#64748b", fontSize: 24 }} />,
      label: "UPCOMING",
      value: stats?.upcoming ?? 0,
      iconBg: "#e2e8f0",
    },
  ];

  return (
    <>
      {/* Top Header - Title and Controls */}
      <Box sx={{ bgcolor: colors.white, borderBottom: `1px solid ${colors.border}` }}>
        <Box sx={{ 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "space-between", 
          px: 3, 
          py: 2.5,
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Box>
            <Typography variant="h5" sx={headerStyles.title}>
              Daily Follow-ups
            </Typography>
            <Typography sx={{ ...headerStyles.pendingBadge, mt: 1 }}>
              <AnimatedCounter value={totalPendingTasks ?? 0} /> PENDING TASKS
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: 'wrap' }}>
            {/* Date Picker Button */}
            <Button
              variant="outlined"
              startIcon={<CalendarToday sx={{ color: "#94A3B8" }} />}
              endIcon={<KeyboardArrowDown sx={{ color: "#94A3B8", fontSize: 18 }} />}
              onClick={handleDateClick}
              sx={headerStyles.dateButton}
            >
              {formatDateButtonLabel()}
            </Button>

            {/* Date Picker Popover */}
            <Popover
              anchorEl={dateAnchorEl}
              open={Boolean(dateAnchorEl)}
              onClose={handleDateClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              PaperProps={{
                sx: datePickerStyles.paper,
              }}
            >
              {/* Quick Options */}
              <Box sx={datePickerStyles.quickOptions}>
                {quickOptions.map((option) => (
                  <Box
                    key={option.value}
                    onClick={() => handleQuickSelect(option)}
                    sx={datePickerStyles.quickOption(option.value === activeQuickOption)}
                  >
                    {option.label}
                  </Box>
                ))}
              </Box>

              {/* Calendar Header */}
              <Box sx={calendarStyles.header}>
                <IconButton onClick={handlePrevMonth} sx={calendarStyles.navButton}>
                  <ChevronLeft fontSize="small" />
                </IconButton>
                <Typography sx={calendarStyles.monthYear}>
                  {MONTHS[currentMonth]} {currentYear}
                </Typography>
                <IconButton onClick={handleNextMonth} sx={calendarStyles.navButton}>
                  <ChevronRight fontSize="small" />
                </IconButton>
              </Box>

              {/* Week Days */}
              <Box sx={calendarStyles.weekDays}>
                {DAYS_OF_WEEK.map((day, index) => (
                  <Typography key={index} sx={calendarStyles.weekDay}>
                    {day}
                  </Typography>
                ))}
              </Box>

              {/* Days Grid */}
              <Box sx={calendarStyles.daysGrid}>
                {getCalendarDays().map((dayInfo, index) => (
                  <Box
                    key={index}
                    onClick={() => handleDayClick(dayInfo)}
                    onMouseEnter={() => handleDayHover(dayInfo)}
                    sx={calendarStyles.day(
                      dayInfo.isSelected,
                      dayInfo.isToday,
                      dayInfo.disabled,
                      dayInfo.isInRange,
                      dayInfo.isRangeStart,
                      dayInfo.isRangeEnd
                    )}
                  >
                    {dayInfo.day}
                  </Box>
                ))}
              </Box>
            </Popover>

            {/* New Task Button */}
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={onNewTask}
              sx={headerStyles.newTaskButton}
            >
              New Task
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Stats Cards Section - Below header, no divider */}
      <Box sx={{  pb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', px: 3, pt: 3 }}>
          {statsData.map((stat, index) => (
            <Fade in timeout={300 + index * 100} key={index}>
              <Paper elevation={0} sx={headerStyles.statCard}>
                <Box sx={{ ...headerStyles.statIconBox, bgcolor: stat.iconBg }}>
                  {stat.icon}
                </Box>
                <Box>
                  <Typography sx={headerStyles.statLabel}>
                    {stat.label}
                  </Typography>
                  <Typography variant="h4" sx={headerStyles.statValue}>
                    <AnimatedCounter value={stat.value} />
                  </Typography>
                </Box>
              </Paper>
            </Fade>
          ))}
        </Box>
      </Box>
    </>
  );
};

export default FollowupHeader;